import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import { NxMessageModule } from '@aposin/ng-aquila/message';
import { NxSpinnerModule } from '@aposin/ng-aquila/spinner';
import { firstValueFrom } from 'rxjs';
import { Application } from '../../models/application.model';
import { EnrichedIncident, Incident, INCIDENT_ENVIRONMENT_DISPLAY_ORDER, IncidentDisplay, IncidentEnvironment, IncidentStatusCode } from '../../models/incident.model';
import { PagerDutyService } from '../../models/pagerDutyService.model';
import { PaginatedResponse } from '../../models/paginated-response.model';
import { IncidentDataService } from '../../services/incident-data.service';
import { IncidentService } from '../../services/incident.service';
import { MockDataService } from '../../services/mock-data.service';
import { PermissionService } from '../../services/permission.service';
import { isLocalEnvironment } from '../../utils/environment.utils';

@Component({
  selector: 'app-incident-communications',
  standalone: true,
  imports: [
    NxSpinnerModule,
    NxMessageModule,
    NxButtonModule,
    DatePipe,
    AsyncPipe,
  ],
  templateUrl: './incident-communications.component.html',
  styleUrl: './incident-communications.component.scss'
})

export class IncidentCommunicationsComponent implements OnInit {
  private readonly mockDataService = inject(MockDataService);
  private readonly incidentService = inject(IncidentService);
  private readonly incidentDataService = inject(IncidentDataService);
  private readonly router = inject(Router);

  permissionService = inject(PermissionService);

  isIncidentsLoading = false;
  isLoadingMore = false;
  currentIncidents: IncidentDisplay[] = [];
  ongoingIncidents: Incident[] = [];
  incidentsMap = new Map<string, EnrichedIncident>();
  allServiceIds: string[] = [];
  loadedServiceIds: string[] = [];
  serviceApplications = new Map<string, Application>()
  servicesMap = new Map<string, PagerDutyService>()
  nextPageToken?:string = undefined
  
  openIncidents: IncidentDisplay[] = [];
  ongoingIncidents_display: IncidentDisplay[] = [];
  resolvedIncidents: IncidentDisplay[] = [];
  closedIncidents: IncidentDisplay[] = [];
  
  IncidentStatusCode = IncidentStatusCode;

  ngOnInit(): void {
    this.loadIncidents();
  }

  async loadIncidents() {
    this.isIncidentsLoading = true;
    this.currentIncidents = [];
    this.ongoingIncidents = [];
    this.openIncidents = [];
    this.ongoingIncidents_display = [];
    this.resolvedIncidents = [];
    this.closedIncidents = [];
    this.incidentsMap.clear();
    this.serviceApplications.clear()
    this.servicesMap.clear()
    this.nextPageToken = undefined
    try {

    const pagerDutyServicesRequest = isLocalEnvironment()
        ? this.mockDataService.getPagerDutyServices()
        : this.incidentService.getPagerDutyServices();

      let response = null;
      try {
        response = await firstValueFrom(pagerDutyServicesRequest);
      } catch (error) {
        console.error('Failed to fetch pagerDutyServices', error);
        throw new Error(`Failed to fetch PagerDuty services: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      const services = response.body as PagerDutyService[]

      services.forEach((service) => {
        // Store the complete service in the map
        this.servicesMap.set(service.pagerDutyServiceId, service);
        
        if(service.applications && service.applications.length > 0) {
          service.applications.forEach((application) => {
            if(!this.serviceApplications.has(application.applicationId)){
              this.serviceApplications.set(application.applicationId, {applicationId: application.applicationId, applicationName: application.applicationName,
                contactEmail: application.contactEmail, features: application.features
              })
            }
          })
        }
      })

      const pagerDutyOngoingIncidents = isLocalEnvironment() ?
        this.mockDataService.getAllOngoingIncidents() :
        this.incidentService.getAllOngoingIncidents()

      let responseIncidents = null
      try {
        responseIncidents = await firstValueFrom(pagerDutyOngoingIncidents)
        const incidents = responseIncidents.body as PaginatedResponse

        this.ongoingIncidents= incidents?.items || [] as Incident[]
        this.currentIncidents = this.mapIncidentsToDisplay(incidents.items || [] as Incident[])
        this.nextPageToken = incidents.nextPageToken

      } catch (error) {
        console.error('Failed to fetch pagerDutyIncidents', error);
        throw new Error(`Failed to fetch PagerDuty incidents: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

      this.sortIncidents()
      this.organizeIncidentsByStatus()


    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred while loading incidents';
      console.error('Error details:', errorMessage);
      // You could add a toast notification or user-facing error message here
    } finally {
      this.isIncidentsLoading = false;
    }
  }

  async loadMoreIncidents() {
    this.isLoadingMore = true;

    try {

      const pagerDutyOngoingIncidentsRequest = isLocalEnvironment() ?
        this.mockDataService.getAllOngoingIncidents(this.nextPageToken) :
        this.incidentService.getAllOngoingIncidents(this.nextPageToken)

      let responseIncidents = null

      try {
        responseIncidents = await firstValueFrom(pagerDutyOngoingIncidentsRequest)

        const incidents = responseIncidents.body as PaginatedResponse
        this.ongoingIncidents = this.ongoingIncidents.concat(incidents.items || [] as Incident[])
        this.currentIncidents = this.currentIncidents.concat(this.mapIncidentsToDisplay(incidents.items || [] as Incident[]))
        this.nextPageToken = incidents.nextPageToken
        this.sortIncidents()
        this.organizeIncidentsByStatus()

      } catch (error) {
        console.error('Failed to fetch pagerDutyIncidents', error);
        throw new Error(`Failed to fetch PagerDuty incidents: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }

    } catch (error) {
      console.error('ERROR: Failed to load more incidents', error);
    } finally {
      this.isLoadingMore = false;
    }
  }

  // Sorts by environment (PROD first, incidents without environment last), then by priority (P1 first),
  // then by most recent start date
  private sortIncidents(): void {
    const environmentRank = (environment?: string): number => {
      const rank = INCIDENT_ENVIRONMENT_DISPLAY_ORDER.indexOf(environment as IncidentEnvironment);
      return rank === -1 ? INCIDENT_ENVIRONMENT_DISPLAY_ORDER.length : rank;
    };
    const priorityRank = (priorityCode?: string): number => {
      const rank = Number(priorityCode?.match(/^P(\d)$/)?.[1]);
      return Number.isNaN(rank) ? Number.MAX_SAFE_INTEGER : rank;
    };

    this.currentIncidents.sort((a, b) => {
      const environmentDifference = environmentRank(a.incidentEnvironmentCode) - environmentRank(b.incidentEnvironmentCode);
      if (environmentDifference !== 0) return environmentDifference;
      const priorityDifference = priorityRank(a.priorityCode) - priorityRank(b.priorityCode);
      if (priorityDifference !== 0) return priorityDifference;
      if (!a.incidentStartDateTime && !b.incidentStartDateTime) return 0;
      if (!a.incidentStartDateTime) return 1;
      if (!b.incidentStartDateTime) return -1;
      return new Date(b.incidentStartDateTime).getTime() - new Date(a.incidentStartDateTime).getTime();
    });
  }

  mapIncidentsToDisplay(incidents: (Incident | EnrichedIncident)[]): IncidentDisplay[] {
    return incidents.map(incident => {
      const firstImpactedApp = incident.impactedApplications?.[0];
      const applicationId = firstImpactedApp?.applicationId;
      const application = applicationId ? this.serviceApplications.get(applicationId) : undefined

      return {
        pagerDutyIncidentId: incident.pagerDutyIncidentId,
        applicationName: application?.applicationName || applicationId || 'Unknown Application',
        incidentEnvironmentCode: incident.incidentEnvironmentCode,
        priorityCode: incident.priorityCode,
        incidentStartDateTime: incident.incidentStartDateTime,
        incidentDescription: incident.incidentDescription,
        simTicketId: incident.serviceIntegrationManagementTicketId ?? 'N/A',
        incidentCommunicationCounter: incident.incidentCommunicationCounter,
        timeUntilRemoval: incident.resolutionCommunicationSentDateTime
          ? this.calculateTimeUntilRemoval(incident.resolutionCommunicationSentDateTime)
          : undefined,
        lastUpdateDateTime: incident.lastUpdateDateTime,
        lastUpdatedby: incident.lastUpdatedby,
      };
    });
  }

  enrichIncident(incident: Incident):EnrichedIncident {
    const service = this.servicesMap.get(incident.pagerDutyServiceId);
    console.log("Pager duty service", service)

    return {
      ...incident,
      service: service
    };
  }

  private calculateTimeUntilRemoval(resolutionSentDateTime: string): string {
    const sentDate = new Date(resolutionSentDateTime);
    const removalDate = new Date(sentDate.getTime() + 24 * 60 * 60 * 1000); // Add 24 hours
    const now = new Date();
    const msRemaining = removalDate.getTime() - now.getTime();

    if (msRemaining <= 0) {
      return 'Expired';
    }

    const hoursRemaining = Math.floor(msRemaining / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));

    return `${hoursRemaining}h ${minutesRemaining}m`;
  }

  private organizeIncidentsByStatus(): void {
    this.openIncidents = [];
    this.ongoingIncidents_display = [];
    this.resolvedIncidents = [];
    this.closedIncidents = [];

    this.currentIncidents.forEach(incident => {
      const fullIncident = this.ongoingIncidents.find(i => i.pagerDutyIncidentId === incident.pagerDutyIncidentId);
      const status = fullIncident?.incidentStatusCode;

      if (!incident.priorityCode) {
        return;
      }

      switch (status) {
        case IncidentStatusCode.OPEN:
          this.openIncidents.push(incident);
          break;
        case IncidentStatusCode.ONGOING:
          this.ongoingIncidents_display.push(incident);
          break;
        case IncidentStatusCode.RESOLVED:
          this.resolvedIncidents.push(incident);
          break;
        case IncidentStatusCode.CLOSED:
          this.closedIncidents.push(incident);
          break;
      }
    });
  }

  onViewIncident(incidentId: string): void {
    const incident = this.ongoingIncidents.find((incident) => incident.pagerDutyIncidentId === incidentId)
   
    if (incident) {
      const enrichedIncident = this.enrichIncident(incident)
      this.incidentDataService.setIncidentData(enrichedIncident);
      this.router.navigate(['/communications-form']);
    }
  }
}
